/**
 * Authentication Service
 *
 * Auth model: httpOnly cookies set by the backend.
 *   - adflow_access   (httpOnly) — access token, sent on every request
 *   - adflow_refresh   (httpOnly) — refresh token, scoped to /api/v1/auth
 *   - adflow_logged_in (JS-readable) — non-sensitive hint for synchronous checks
 *
 * The frontend never stores tokens. The JS-readable hint cookie is set by the
 * backend alongside the httpOnly tokens — no localStorage involved.
 */

import { api } from './api';

/**
 * Clear the JS-readable hint cookie.
 *
 * Called on logout and when auth validation fails, so the synchronous
 * isAuthenticated() check returns false immediately (before the next
 * page load or route transition).
 */
export function clearHintCookie() {
  document.cookie = 'adflow_logged_in=; Max-Age=0; Path=/; SameSite=Lax';
}

export const authService = {
  /**
   * Login user.
   * Backend sets httpOnly cookies; no client-side storage needed.
   */
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  /**
   * Verify MFA challenge during login.
   * Backend sets httpOnly cookies on success.
   */
  verifyMFAChallenge: async (mfaToken, code) => {
    const response = await api.post('/auth/2fa/challenge', {
      mfa_token: mfaToken,
      code,
    });
    return response.data;
  },

  /**
   * Register new user.
   * Backend sets httpOnly cookies; no client-side storage needed.
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  /**
   * Logout user.
   * Backend clears httpOnly cookies; we clear the JS hint cookie.
   */
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearHintCookie();
    }
  },

  /**
   * Get current user profile from /auth/me.
   * The httpOnly access cookie is sent automatically.
   */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Check if user is likely authenticated (synchronous hint).
   *
   * Reads the non-httpOnly `adflow_logged_in` cookie set by the backend.
   * This is a UI-level check only — real validation happens server-side
   * when the httpOnly access cookie is sent with /auth/me.
   */
  isAuthenticated: () => {
    return document.cookie.split(';').some(c => c.trim().startsWith('adflow_logged_in='));
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  /**
   * Get social login authorize URL for a provider (google | microsoft)
   */
  socialAuthorize: async (provider) => {
    const response = await api.get(`/auth/social/authorize?provider=${provider}`);
    return response.data;
  },
};

export default authService;
